/**
 * BookshelfView — a Estante Odisseia como CAROUSEL: um livro selecionado por
 * vez, sempre centralizado, com os vizinhos menores/atrás (coverflow sutil).
 * O livro central fica sempre ENTRE-ABERTO (a mesma animação de "caderno
 * Moleskine" — rotateZ leve no caderno + a capa abre em rotateY revelando a
 * página de papel com metadados/ações); ao trocar a seleção, o antigo fecha
 * e o novo abre automaticamente.
 *
 * O MOTOR do carousel é o Embla Carousel (mesmo engine usado pelo Carousel
 * do shadcn/ui) — drag/swipe/momentum/snap/supressão de clique-fantasma
 * pós-arraste são 100% dele; aqui só cuidamos do visual (escala/opacidade
 * dos vizinhos, abertura do central) e da geometria (slot largo o bastante
 * pra capa aberta não invadir o vizinho). Navegação: setas ‹ › (desktop,
 * sempre visíveis), teclado ←/→, clique num vizinho seleciona ele.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Copy,
  Share2,
  Download,
  PenLine,
} from 'lucide-react';
import { GiBookCover, GiDeathNote, GiEvilBook } from './icons/game-icons';
import { useAppStore } from '../stores/app-store';
import { useIsMobile } from '../hooks/use-mobile';
import { BookCover } from './book-cover';
import { BookMetadataModal } from './book-metadata-modal';
import { notify, toast } from '../lib/toast';
import { cn } from '../lib/utils';
import type { Project } from '../types';

const NOVEL_TARGET = 80_000;
const WORDS_PER_PAGE = 275;

function wordsOf(p: Project): number {
  return p.chapters.reduce((s, c) => s + c.wordCount, 0);
}
function progressOf(p: Project): number {
  return Math.min(100, Math.round((wordsOf(p) / NOVEL_TARGET) * 100));
}
function pagesOf(p: Project): number {
  return Math.max(1, Math.round(wordsOf(p) / WORDS_PER_PAGE));
}

// página de papel por baixo da capa: LISA (gradiente suave, sem pauta/grade
// — texturas foram rejeitadas pelo usuário).

// ── geometria do coverflow ──────────────────────────────────────────────
// largura de cada slide do Embla (bem maior que o caderno em si — é esse
// respiro extra que garante que a capa aberta do central, girada em
// rotateY, tenha espaço próprio e nunca pinte por cima do vizinho) e até
// que distância do selecionado um vizinho ainda aparece. Constantes por
// breakpoint (o resto — posição/drag/snap — é 100% do Embla).
const GEOM = {
  desktop: { slideW: 330, maxSide: 4 },
  mobile: { slideW: 184, maxSide: 3 },
};
// escala/opacidade dos vizinhos — decaimento suave e harmonioso (não
// abrupto) à medida que se afastam do centro, até sumir de vez. Os vizinhos
// próximos ficam só LEVEMENTE menores/recuados (não apagados demais — eles
// dão contexto de "estante cheia"); só os distantes desbotam de fato.
const SCALE_STEPS = [1, 0.83, 0.72, 0.63, 0.57] as const;
const OPACITY_STEPS = [1, 0.9, 0.7, 0.42, 0] as const;
function scaleFor(absOffset: number): number {
  return SCALE_STEPS[Math.min(absOffset, SCALE_STEPS.length - 1)];
}
function opacityFor(absOffset: number, maxSide: number): number {
  if (absOffset > maxSide) return 0;
  return OPACITY_STEPS[Math.min(absOffset, OPACITY_STEPS.length - 1)];
}

// menor distância (com sinal) de `i` até `selected` NUM CÍRCULO de `len`
// posições — só usada pro visual (escala/opacidade); a navegação em si
// (posição/drag/loop) é toda do Embla.
function circularOffset(i: number, selected: number, len: number): number {
  let offset = i - selected;
  if (offset > len / 2) offset -= len;
  if (offset < -len / 2) offset += len;
  return offset;
}

// prefers-reduced-motion — o Embla anima o scroll com `duration` (em
// "frames" internos); zerar faz o snap ser instantâneo, sem a animação de
// momentum/easing (mesma checagem usada em components/moments.tsx).
const prefersReducedMotion =
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

interface ActionMenuProps {
  t: (key: string, fallback: string) => string;
  onEditDetails: () => void;
  onChangeCover: () => void;
  onDuplicate: () => void;
  onShare: () => void;
  onExport: () => void;
  onDelete: () => void;
}

// menu de ações (⋯) — extraído do render pra não remontar a cada re-render
// do pai (perde foco/pisca se um componente inline fosse recriado toda hora).
function ActionMenu({
  t,
  onEditDetails,
  onChangeCover,
  onDuplicate,
  onShare,
  onExport,
  onDelete,
}: ActionMenuProps) {
  return (
    <div className="bk-menu" onClick={(e) => e.stopPropagation()}>
      <button className="bk-mi" type="button" onClick={onEditDetails}>
        <Pencil /> {t('bookDetail.editDetails', 'Editar Detalhes')}
      </button>
      <button className="bk-mi" type="button" onClick={onChangeCover}>
        <GiBookCover /> {t('bookDetail.changeCover', 'Trocar Capa')}
      </button>
      <button className="bk-mi" type="button" onClick={onDuplicate}>
        <Copy /> {t('bookDetail.duplicate', 'Duplicar')}
      </button>
      <button className="bk-mi" type="button" onClick={onShare}>
        <Share2 /> {t('bookDetail.share', 'Compartilhar')}
      </button>
      <button className="bk-mi" type="button" onClick={onExport}>
        <Download /> {t('nav.export', 'Exportar')}
      </button>
      <button className="bk-mi danger" type="button" onClick={onDelete}>
        <GiDeathNote /> {t('bookDetail.deleteBook', 'Excluir Livro')}
      </button>
    </div>
  );
}

export function BookshelfView() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'pt-BR';
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { projects, setCurrentProject, duplicateProject, deleteProject } = useAppStore();
  const geom = isMobile ? GEOM.mobile : GEOM.desktop;
  const bookW = isMobile ? 152 : 206;

  const ordered = useMemo(
    () => [...projects].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [projects]
  );

  // ── estática vs carousel: medida REAL, não número mágico ───────────────
  // `.msk-carousel` tem width:100% independente de quantos livros existem
  // (é a largura da coluna de conteúdo da página), então medir a LARGURA
  // DELE (via ResizeObserver, atualizado em todo resize/reflow) e comparar
  // com a largura que a fileira de livros de fato ocupa diz com certeza se
  // eles cabem sem transbordar. Quando cabem: fileira ESTÁTICA centrada
  // (sem Embla/setas/drag — ver JSX abaixo). Quando não cabem: Embla com
  // loop, como antes. Isso também é a CAUSA RAIZ do bug de descentralização
  // com poucos livros: o Embla desativa `loop` sozinho quando o conteúdo
  // não preenche pelo menos 2x o viewport (ver `SlideLooper.canLoop()` no
  // engine), e SEM loop e com pouca folga o `align:'center'` fica travado
  // pelos limites do scroll (não há conteúdo suficiente pra "puxar" o
  // slide até o centro sem sobrar vazio na ponta) — o resultado visual é
  // idêntico ao bug original (fileira grudada numa borda). Não dá pra
  // "consertar" isso pedindo pro Embla centralizar sem loop — a solução
  // correta é nem usar o Embla nesse caso.
  //
  // A largura real da fileira estática NÃO é `n * slideW`: só o caderno
  // SELECIONADO (o único que de fato abre) precisa da folga extra de
  // `slideW` pra capa girar em rotateY sem invadir o vizinho; os demais,
  // fechados, ocupam só a própria largura (`bookW`) + um respiro pequeno
  // entre eles. Contar a folga cheia pra TODOS (como no carousel, que
  // precisa dela pra qualquer um virar central) exagerava a largura da
  // fileira estática — e é exatamente essa folga inflada que fazia até um
  // par de livros no mobile "transbordar" por pouquíssimos pixels e cair
  // no carousel (câmbio de modo com resultado feio, o próprio bug acima).
  const STATIC_GAP = isMobile ? 12 : 46;
  const shelfMeasureRef = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  useLayoutEffect(() => {
    const el = shelfMeasureRef.current;
    if (!el) return;
    setRowWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setRowWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // largura da fileira estática = TODOS os slots com a MESMA largura fixa
  // (`bookW`), não mais um slot inflado (`slideW`) pro selecionado. Isso é a
  // CAUSA RAIZ do "abrir empurrando os vizinhos": o slot do selecionado
  // reservava `slideW` (~300px) enquanto os fechados ocupavam só `bookW`
  // (~206px); ao trocar a seleção, o slot que passava a ser o central crescia
  // e o antigo encolhia — o flexbox refluía a fileira inteira e todo mundo
  // andava de lado. Com largura idêntica pra todos, mudar QUAL é o central não
  // altera nenhuma caixa de layout (z-index e transforms não refluem), então
  // ninguém se move; a capa aberta apenas TRANSBORDA por cima do vizinho (via
  // z-index do `.is-center`), como um livro real abrindo na estante. Bônus: a
  // fileira fica `slideW - bookW` (~94px) mais estreita, então MAIS livros
  // cabem no modo estático (menos casos caindo no carousel). */
  const staticRowWidth =
    ordered.length === 0 ? 0 : ordered.length * bookW + Math.max(0, ordered.length - 1) * STATIC_GAP;
  const isStatic = rowWidth > 0 && staticRowWidth <= rowWidth;

  // ── Embla: motor do carousel (drag/swipe/momentum/snap/loop) — só
  // "conectado" (ref anexada) quando NÃO estamos no modo estático; o hook
  // em si é sempre chamado (regra dos hooks), mas sem a ref anexada a um
  // nó real o Embla nunca inicializa (emblaApi fica undefined). ────────
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
    // `duration` do Embla é em "frames" internos (não ms) — 30 dá um scroll
    // com peso/momentum suave (o default 25 era mais "seco"), casando com o
    // easing macio das capas/escala (ver .msk-* no CSS).
    duration: prefersReducedMotion ? 0 : 30,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  // espelha `selectedIndex` num ref pra ler o valor mais recente de dentro
  // de um efeito sem precisar dele nas deps (ver uso logo abaixo) — escrever
  // num ref durante o render é seguro (não afeta ESTE render, só leituras
  // futuras) e evita reexecutar o efeito a cada seleção.
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [metaProject, setMetaProject] = useState<Project | null>(null);
  const [metaTab, setMetaTab] = useState<'basic' | 'cover'>('basic');
  const pageMenuRef = useRef<HTMLDivElement>(null);
  const cornerMenuRef = useRef<HTMLDivElement>(null);

  // sincroniza nosso índice selecionado com o estado real do Embla. 'select'
  // é sempre INTENÇÃO real do usuário (drag, seta, teclado, clique num
  // vizinho via scrollTo) — aceita direto. 'reInit' é estrutural (a lista de
  // projetos mudou, o container/slide mudou de tamanho num resize, ou é o
  // primeiro mount depois do modo estático) e NUNCA representa uma escolha
  // do usuário — então, em vez de aceitar cegamente o índice que o Embla
  // decidiu sozinho, restauramos explicitamente o que já estava selecionado.
  // Isso importa de verdade: recalcular a geometria (resize) pode fazer o
  // Embla, momentaneamente, deixar de "caber" o loop com a nova largura
  // (mesma causa raiz do bug de descentralização — `canLoop()` falha) e ele
  // reresolve sozinho pro índice 0, perdendo a seleção do usuário no meio
  // de um resize (bug real, pego neste harness). `selectedIndexRef` (ver
  // acima) é a fonte da verdade da intenção do usuário; toda vez que o
  // Embla "esquece" e diverge dela, é ele que cede, não o contrário.
  useEffect(() => {
    if (!emblaApi) return;
    // clampa a seleção desejada contra a contagem de slides que o Embla
    // REALMENTE tem no DOM (`slideNodes().length`) — não `scrollSnapList()`.
    // Motivo: durante um resize (troca mobile↔desktop, --msk-slide-w muda),
    // o ResizeObserver interno do Embla pode disparar um 'reInit' num
    // instante intermediário onde a geometria ainda não terminou de
    // assentar, e o motor computa transitoriamente MENOS scroll snaps do
    // que slides de verdade existem (visto ao vivo neste harness: 3 livros,
    // `scrollSnapList()` momentaneamente com só 1 entrada). Se confiássemos
    // nesse número pra clampar, "esqueceríamos" a seleção real por causa de
    // um instante de medição capenga — não porque a lista de fato mudou.
    // `slideNodes().length` é sempre a contagem verdadeira (reflete o DOM
    // atual, não o cálculo de geometria que pode estar temporariamente
    // errado). Só ignoramos o reInit inteiro (não tocamos no índice) quando
    // os scroll snaps ainda nem alcançaram essa contagem — sinal claro de
    // medição capenga em andamento; um reInit seguinte, já assentado,
    // corrige sozinho.
    const restoreDesiredIndex = () => {
      const slideCount = emblaApi.slideNodes().length;
      if (emblaApi.scrollSnapList().length < slideCount) return;
      const maxIndex = Math.max(0, slideCount - 1);
      const desired = Math.min(selectedIndexRef.current, maxIndex);
      if (emblaApi.selectedScrollSnap() !== desired) emblaApi.scrollTo(desired, true);
    };
    restoreDesiredIndex();
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setMenuOpen(false);
    };
    const onReInit = () => {
      const slideCount = emblaApi.slideNodes().length;
      if (emblaApi.scrollSnapList().length < slideCount) return;
      const maxIndex = Math.max(0, slideCount - 1);
      const desired = Math.min(selectedIndexRef.current, maxIndex);
      if (emblaApi.selectedScrollSnap() !== desired) emblaApi.scrollTo(desired, true);
      else onSelect();
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi]);

  // cursor grab/grabbing durante o drag — puramente estético, o próprio
  // Embla já cuida de toda a mecânica do arraste.
  useEffect(() => {
    if (!emblaApi) return;
    const onDown = () => setIsPointerDown(true);
    const onUp = () => setIsPointerDown(false);
    emblaApi.on('pointerDown', onDown);
    emblaApi.on('pointerUp', onUp);
    return () => {
      emblaApi.off('pointerDown', onDown);
      emblaApi.off('pointerUp', onUp);
    };
  }, [emblaApi]);

  // fecha o menu ⋯ clicando fora
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePage = pageMenuRef.current?.contains(target);
      const insideCorner = cornerMenuRef.current?.contains(target);
      if (!insidePage && !insideCorner) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  // teclado ←/→ troca a seleção (ignora se o foco estiver num campo de
  // texto/textarea ou se um menu/modal estiver aberto por cima). No modo
  // estático não há Embla pra navegar — troca `selectedIndex` direto (sem
  // dar a volta, já que a fileira inteira já está visível).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMenuOpen(false); return; }
      if (menuOpen || metaProject) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (active as HTMLElement)?.isContentEditable) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (isStatic) setSelectedIndex((i) => Math.max(0, i - 1));
        else emblaApi?.scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (isStatic) setSelectedIndex((i) => Math.min(ordered.length - 1, i + 1));
        else emblaApi?.scrollNext();
      }
    };
    addEventListener('keydown', h);
    return () => removeEventListener('keydown', h);
  }, [menuOpen, metaProject, emblaApi, isStatic, ordered.length]);

  // ── fechar = espelho exato do abrir ────────────────────────────────────
  // a abertura é 100% transição CSS (a capa gira em rotateY, o caderno
  // inclina), então a REVERSA já seria automática quando o livro perde
  // `.is-open` — EXCETO por um detalhe: a página de papel revelada (.msk-page,
  // com título/stats/ações) só era MONTADA pro livro central. No instante em
  // que a seleção troca, o antigo deixa de ser central e o React DESMONTA a
  // página na hora — o conteúdo revelado some seco (pisca) enquanto a capa
  // ainda está no meio do caminho voltando a fechar. Pra que o fechamento seja
  // idêntico ao abrir (mesma .52s, mesmo easing), a página precisa continuar
  // montada até a capa terminar de se fechar sobre ela. `closingIds` mantém o
  // id do(s) livro(s) que acabaram de perder a seleção montados pelo tempo da
  // transição; cada um tem seu próprio timer (Set-like), então rajadas de
  // troca (A→B→C) fecham suaves em paralelo em vez de piscar. Chaveado por
  // `id` (não índice) pra sobreviver a reordenação da lista. */
  const selectedIdForClose = ordered.length
    ? ordered[Math.min(selectedIndex, ordered.length - 1)]?.id
    : undefined;
  const [closingIds, setClosingIds] = useState<string[]>([]);
  const prevSelectedIdRef = useRef(selectedIdForClose);
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedIdForClose;
    if (!prev || prev === selectedIdForClose) return;
    // o antigo central entra em "fechando"; o novo central, se por acaso ainda
    // constar como fechando (troca rápida de volta), é removido da lista.
    setClosingIds((ids) => {
      const next = ids.filter((x) => x !== selectedIdForClose);
      return next.includes(prev) ? next : [...next, prev];
    });
    // .52s da transição + folga pra ela assentar antes de desmontar a página.
    const timer = setTimeout(() => {
      setClosingIds((ids) => ids.filter((x) => x !== prev));
    }, 560);
    return () => clearTimeout(timer);
  }, [selectedIdForClose]);

  // ── expansão da página em SINCRONIA com a capa ─────────────────────────
  // a página de papel é MAIS LARGA que o caderno (salta à direita do vinco pra
  // expor o conteúdo). Antes ela era montada já no tamanho EXPANDIDO e ficava
  // assim até o `closingIds` desmontá-la de uma vez — no fechamento a capa
  // voltava a fechar mas a página só sumia SUBITAMENTE no fim (o strip
  // saliente ficava pra fora da capa fechada = a animação "feia"). Agora a
  // geometria RECOLHIDA (footprint exato do caderno, 100% atrás da capa) é o
  // DEFAULT da `.msk-page`; a classe `.is-expanded` aplica a geometria
  // saliente e é o que TRANSICIONA (mesma .52s/easing da capa). Ao fechar,
  // `isCenter` vira false e `.is-expanded` cai no MESMO render em que a capa
  // perde `.is-open` — página e capa recolhem/fecham JUNTAS, sem discrepância.
  // Na ABERTURA a página é montada RECOLHIDA e só ganha `.is-expanded` UM
  // FRAME depois (via rAF): transições CSS não disparam no próprio mount, então
  // esse frame extra é o que faz a largura animar a partir do estado recolhido
  // — espelhando a capa que abre, em vez de já nascer expandida (flash do strip).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedIdForClose) {
      setExpandedId(null);
      return;
    }
    const raf = requestAnimationFrame(() => setExpandedId(selectedIdForClose));
    return () => cancelAnimationFrame(raf);
  }, [selectedIdForClose]);

  function selectOrOpen(i: number, p: Project) {
    if (i === selectedIndex) {
      openBook(p);
    } else {
      setMenuOpen(false);
      if (isStatic) setSelectedIndex(i);
      else emblaApi?.scrollTo(i);
    }
  }

  function openBook(p: Project) {
    setCurrentProject(p.id);
    navigate(`/app/${p.kind === 'manga' ? 'manga' : 'editor'}/${p.id}`);
  }

  async function duplicate(p: Project) {
    setMenuOpen(false);
    await duplicateProject(p.id);
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'twin', title: p.metadata.title || '' } }));
  }
  function share(p: Project) {
    setMenuOpen(false);
    void navigator.clipboard?.writeText(`${location.origin}/app/editor/${p.id}`);
    notify.success(t('estante.linkCopied', 'Link do livro copiado'));
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'feather' } }));
  }
  function removeBook(p: Project) {
    setMenuOpen(false);
    const endedTitle = p.metadata.title || '';
    toast(t('projects.deleteConfirm', 'Tem certeza que deseja excluir este projeto?'), {
      icon: <GiEvilBook size={16} />,
      description: endedTitle,
      action: {
        label: t('common.delete', 'Excluir'),
        onClick: async () => {
          await deleteProject(p.id);
          window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'unbind', title: endedTitle } }));
          notify.deleted(endedTitle || t('projects.untitled', 'Sem título'));
        },
      },
      cancel: {
        label: t('common.cancel', 'Cancelar'),
        onClick: () => {},
      },
      duration: 10000,
    });
  }

  if (ordered.length === 0) return null;

  const clampedIndex = Math.min(selectedIndex, ordered.length - 1);
  const selected = ordered[clampedIndex];
  // carousel circular — as setas só desabilitam quando não há pra onde ir
  // (um único livro); do contrário sempre "dá a volta" (Embla com loop:true).
  const canNavigate = ordered.length > 1;

  const menuNode = selected && (
    <ActionMenu
      t={t}
      onEditDetails={() => { setMenuOpen(false); setMetaTab('basic'); setMetaProject(selected); }}
      onChangeCover={() => { setMenuOpen(false); setMetaTab('cover'); setMetaProject(selected); }}
      onDuplicate={() => duplicate(selected)}
      onShare={() => share(selected)}
      onExport={() => { setMenuOpen(false); navigate(`/app/export/${selected.id}`); }}
      onDelete={() => removeBook(selected)}
    />
  );

  return (
    <div className="msk-shelf-outer">
      {/* fundo ambiente — atrás de tudo (z-index:-1), pointer-events none;
          3 orbes de gradiente derivando + vinheta + grão. Ver bloco
          `.msk-ambient` em styles.css. */}
      <div className="msk-ambient" aria-hidden>
        <span className="msk-orb msk-orb-1" />
        <span className="msk-orb msk-orb-2" />
        <span className="msk-orb msk-orb-3" />
      </div>
      <div
        className="msk-carousel"
        ref={shelfMeasureRef}
        style={{
          '--msk-h': isMobile ? '215px' : '295px',
          '--msk-w': isMobile ? '152px' : '206px',
          '--msk-slide-w': `${geom.slideW}px`,
        } as CSSProperties}
      >
        {/* prateleira — superfície sob os cadernos, alinhada com a BASE deles
            (mesmo pivô 0/100% do --msk-scale); fora de `.msk-track` de
            propósito (o track tem overflow:hidden, mas a prateleira deve
            atravessar a largura inteira do carousel, não ser clipada). */}
        <span className="msk-shelf-ledge" aria-hidden />

        {!isStatic && (
          <>
            <button
              type="button"
              className="msk-arrow msk-arrow-l"
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canNavigate}
              aria-label={t('estante.scrollPrev', 'Livro anterior')}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="msk-arrow msk-arrow-r"
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNavigate}
              aria-label={t('estante.scrollNext', 'Próximo livro')}
            >
              <ChevronRight />
            </button>
          </>
        )}

        <div
          className={cn('msk-track', isStatic && 'is-static', isPointerDown && 'is-dragging')}
          ref={isStatic ? undefined : emblaRef}
        >
          <div className={cn('msk-embla-container', isStatic && 'is-static')}>
            {ordered.map((p, i) => {
              const offset = isStatic ? 0 : circularOffset(i, clampedIndex, ordered.length);
              const abs = isStatic ? 0 : Math.abs(offset);
              const isCenter = isStatic ? i === clampedIndex : offset === 0;
              const scale = isStatic ? 1 : scaleFor(abs);
              const opacity = isStatic ? 1 : opacityFor(abs, geom.maxSide);
              const title = p.metadata.title || t('projects.untitled', 'Sem título');
              const pct = progressOf(p);
              const last = new Date(p.updatedAt).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
              const isMenuOpen = isCenter && menuOpen;
              // página revelada montada enquanto o livro é central OU está no
              // meio da animação de FECHAMENTO (ver `closingIds`) — evita o
              // "pisca" de o conteúdo sumir seco antes de a capa terminar de
              // fechar sobre ele.
              const isClosing = !isCenter && closingIds.includes(p.id);
              const showPage = isCenter || isClosing;

              return (
                <div
                  key={p.id}
                  // `.is-center` fica por cima de tudo (capa aberta transborda
                  // sobre o vizinho sem empurrá-lo); o que está FECHANDO fica
                  // logo abaixo do central mas acima dos vizinhos normais, pra
                  // continuar cobrindo o vizinho enquanto a capa se fecha.
                  className={cn('msk-embla-slide', isCenter && 'is-center', isClosing && 'is-closing')}
                  // modo estático: TODOS os slots com a MESMA largura fixa
                  // (`bookW`), selecionado ou não — trocar a seleção não muda
                  // nenhuma caixa de layout, então ninguém é empurrado (ver
                  // comentário em `staticRowWidth`). A capa aberta transborda
                  // por cima do vizinho via z-index, sem cortar (o track fica
                  // com overflow visível no modo estático).
                  style={isStatic ? { flex: `0 0 ${bookW}px` } : undefined}
                >
                  <div className={cn('msk-slot', isCenter && 'is-center')}>
                    <div
                      className={cn('msk-book', isCenter && 'is-open')}
                      style={{ '--msk-scale': scale, opacity, pointerEvents: opacity <= 0.02 ? 'none' : undefined } as CSSProperties}
                      role="button"
                      tabIndex={abs <= geom.maxSide ? 0 : -1}
                      aria-label={title}
                      aria-current={isCenter}
                      // no modo estático não faz sentido "seguir o foco" — todos
                      // os livros já estão totalmente visíveis/no tamanho normal
                      // (ninguém fica pequeno/rotacionado esperando o foco), então
                      // só clique/Enter/Espaço selecionam (`selectOrOpen` abaixo).
                      // No carousel, `emblaApi.scrollTo` é assíncrono (a troca do
                      // `selectedIndex` real só chega depois, via o listener
                      // `select` do Embla) — por isso não conflita com o clique
                      // que vem em seguida. Se aqui também setasse o estado direto
                      // (como no modo estático), o React re-renderizaria ENTRE o
                      // foco e o clique do mesmo gesto, e o onClick abaixo veria
                      // `selectedIndex` já igual a `i` e abriria o livro no lugar
                      // de só selecioná-lo (bug real, pego neste harness).
                      onFocus={() => {
                        if (isStatic || isCenter) return;
                        emblaApi?.scrollTo(i);
                      }}
                      onClick={() => selectOrOpen(i, p)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOrOpen(i, p); }
                      }}
                    >
                      {/* página de papel — montada pro livro central E pro que
                          está FECHANDO (durante a animação reversa), pra a capa
                          voltar a se fechar suavemente sobre um conteúdo real em
                          vez de sobre o vazio (senão o texto some seco = pisca).
                          Enquanto só fecha (não é central), fica `inert` pra não
                          capturar foco/clique por trás da capa que a cobre. */}
                      {showPage && (
                        <div
                          className={cn('msk-page', isCenter && expandedId === p.id && 'is-expanded')}
                          {...(isClosing ? { inert: true } : {})}
                        >
                          {/* miolo em largura de design fixa (250×295 = página
                              expandida do desktop); no mobile é reduzido por
                              transform:scale — layout idêntico, só menor, sem
                              reflow. Ver `.msk-page-inner` em styles.css. */}
                          <div className="msk-page-inner">
                          <p className="bk-eyebrow">{t('estante.whereStopped', 'Onde você parou')}</p>
                          <h5 className="bk-page-title">{title}</h5>
                          <span className="bk-page-rule" aria-hidden />
                          <div className="bk-stat"><span>{t('projects.chapters', 'capítulos')}</span><b>{p.chapters.length}</b></div>
                          <div className="bk-stat"><span>{t('estante.pages', 'páginas')}</span><b>{pagesOf(p).toLocaleString(locale)}</b></div>
                          <div className="bk-stat"><span>{t('estante.lastSession', 'Última sessão')}</span><b>{last}</b></div>
                          <div className="bk-track"><div className="bk-fill" style={{ width: `${pct}%` }} /></div>
                          <div className="bk-stat" style={{ marginTop: 6 }}><span>{t('estante.manuscript', 'Manuscrito')}</span><b>{pct}%</b></div>

                          <div className="bk-actions" ref={isMenuOpen ? pageMenuRef : undefined}>
                            <button
                              className="bk-btn bk-btn-pri"
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openBook(p); }}
                              title={t('bookDetail.openInEditor', 'Abrir no Editor')}
                              aria-label={t('bookDetail.openInEditor', 'Abrir no Editor')}
                            >
                              <PenLine />
                              {t('projectsView.write', 'Escrever')}
                            </button>
                            <button
                              className="bk-btn bk-btn-gho"
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                              title={t('messagesView.moreOptions', 'Mais opções')}
                              aria-label={t('messagesView.moreOptions', 'Mais opções')}
                              aria-expanded={isMenuOpen}
                            >
                              <MoreHorizontal />
                            </button>
                            {isMenuOpen && menuNode}
                          </div>
                          </div>
                        </div>
                      )}

                      {/* capa — a NOSSA capa (imagem ou gradiente+título), abre em rotateY */}
                      <div className="msk-cover-clip">
                        <BookCover
                          title={title}
                          author={p.metadata.author || ''}
                          genre={p.metadata.genre || 'Fiction'}
                          coverImage={p.metadata.coverImage}
                          variant="cover"
                          progress={pct}
                          seed={p.id}
                          year={new Date(p.createdAt).getFullYear()}
                          disableTilt
                          className="w-full h-full !rounded-none"
                        />
                      </div>

                      {/* mobile: botão ⋯ só no livro central (é o único "ativo") */}
                      {isCenter && (
                        <div className="msk-corner">
                          <button
                            type="button"
                            className="msk-corner-btn"
                            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                            aria-label={t('messagesView.moreOptions', 'Mais opções')}
                            aria-expanded={isMenuOpen}
                          >
                            <MoreHorizontal />
                          </button>
                          {isMenuOpen && (
                            // preserve-3d aqui é necessário: sem isso este wrapper
                            // (pai direto do .bk-menu) "achata" o translateZ dele
                            // antes que o preserve-3d do .msk-corner tenha efeito
                            // — era exatamente o elo que faltava pro menu parar
                            // de pintar atrás da capa girando.
                            <div ref={cornerMenuRef} style={{ transformStyle: 'preserve-3d' }}>
                              {menuNode}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* sombra de contato — dá apoio "de prateleira"; fica fora de
                        `.msk-book` de propósito (não deve tombar junto quando o
                        central abre). */}
                    <span
                      className="msk-shadow"
                      style={{ width: bookW * scale * 0.8, opacity: isCenter ? 0.85 : opacity * 0.55 }}
                      aria-hidden
                    />

                    <h4 className={cn('msk-title', isCenter && 'is-center')} title={title}>{title}</h4>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* navegação discreta: dots quando são poucos livros (≤12), contador
          textual minúsculo quando são muitos (dots viram poeira). */}
      {ordered.length > 1 && ordered.length <= 12 && (
        <div className="msk-dots" role="tablist" aria-label={t('estante.shelf', 'Estante')}>
          {ordered.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className={cn('msk-dot', i === clampedIndex && 'is-active')}
              aria-label={p.metadata.title || t('projects.untitled', 'Sem título')}
              aria-selected={i === clampedIndex}
              role="tab"
              onClick={() => { if (isStatic) setSelectedIndex(i); else emblaApi?.scrollTo(i); }}
            />
          ))}
        </div>
      )}
      {ordered.length > 12 && (
        <p className="msk-counter">
          {t('estante.counterOf', '{{current}} de {{total}}', { current: clampedIndex + 1, total: ordered.length })}
        </p>
      )}

      {/* editar detalhes / trocar capa — modal real do app */}
      {metaProject && (
        <BookMetadataModal
          project={metaProject}
          open={!!metaProject}
          initialTab={metaTab}
          onOpenChange={(open) => { if (!open) setMetaProject(null); }}
        />
      )}
    </div>
  );
}
